<template>
  <div class="mt-6">
    <div class="border border-gray-200 rounded-lg overflow-hidden text-sm">
      <!-- Header -->
      <div class="flex bg-gray-50 border-b border-gray-200">
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Recruiting Info
        </div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">Value</div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Last Updated
        </div>
      </div>
      <!-- GPA -->
      <PlayerInfoRow
        :label="readModel.gpa.label"
        :last-updated="formRows.gpa.lastUpdated"
        :display="readModel.gpa.display"
        :is-editing="isEditing"
        data-testid="gpa-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputNumber
            v-model="formRows.gpa.data"
            :min-fraction-digits="0"
            :max-fraction-digits="2"
            :min="0"
            :max="10"
            class="w-full input-number-fix"
            show-buttons
          />
        </template>
        <template #display>
          {{ readModel.gpa.data }}
        </template>
      </PlayerInfoRow>
      <!-- SAT Score -->
      <PlayerInfoRow
        :label="readModel.satScore.label"
        :last-updated="formRows.satScore.lastUpdated"
        :display="readModel.satScore.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="sat-score-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputNumber
            v-model="formRows.satScore.data"
            :min="400"
            :max="1600"
            class="w-full input-number-fix"
            show-buttons
          />
        </template>
        <template #display>
          {{ readModel.satScore.data }}
        </template>
      </PlayerInfoRow>
      <!-- ACT Score -->
      <PlayerInfoRow
        :label="readModel.actScore.label"
        :last-updated="formRows.actScore.lastUpdated"
        :display="readModel.actScore.display"
        :is-editing="isEditing"
        data-testid="act-score-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputNumber
            v-model="formRows.actScore.data"
            :min="1"
            :max="36"
            show-buttons
            class="w-full input-number-fix"
          />
        </template>
        <template #display>
          {{ readModel.actScore.data }}
        </template>
      </PlayerInfoRow>
      <!-- NCAA Eligibility Center ID -->
      <PlayerInfoRow
        :label="readModel.ncaaEligibilityCenterId.label"
        :last-updated="formRows.ncaaEligibilityCenterId.lastUpdated"
        :display="readModel.ncaaEligibilityCenterId.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="ncaa-eligibility-center-id-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.ncaaEligibilityCenterId.data"
            v-keyfilter.int
            :invalid="v$.ncaaEligibilityCenterId.$invalid"
            maxlength="10"
            class="w-full"
          />
        </template>
        <template #display>
          {{ readModel.ncaaEligibilityCenterId.data }}
        </template>
      </PlayerInfoRow>
      <!-- College Commitment -->
      <PlayerInfoRow
        :label="readModel.collegeCommitment.label"
        :last-updated="formRows.collegeCommitment.lastUpdated"
        :display="readModel.collegeCommitment.display"
        :is-editing="isEditing"
        data-testid="college-commitment-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.collegeCommitment.data"
            maxlength="200"
            class="w-full"
          />
        </template>
        <template #display>
          {{ readModel.collegeCommitment.data }}
        </template>
      </PlayerInfoRow>
      <!-- Highlights/Recruiting Profile Link -->
      <PlayerInfoRow
        :label="readModel.highlightsRecruitingProfileLink.label"
        :last-updated="formRows.highlightsRecruitingProfileLink.lastUpdated"
        :display="readModel.highlightsRecruitingProfileLink.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="highlights-recruiting-profile-link-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.highlightsRecruitingProfileLink.data"
            :invalid="v$.highlightsRecruitingProfileLink.$invalid"
            maxlength="200"
            class="w-full"
          />
        </template>
        <template #display>
          {{ readModel.highlightsRecruitingProfileLink.data }}
        </template>
      </PlayerInfoRow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import PlayerInfoRow from "../PlayerInfoRow.vue";
import type { RecruitingInfoFormRows } from "./types";
import * as filter from "../filter";
import useVuelidate from "@vuelidate/core";
import { maxLength, minLength, numeric, url } from "@vuelidate/validators";

const props = defineProps<{
  isEditing: boolean;
  searchQuery: string | null | undefined;
}>();

const formRows = defineModel<RecruitingInfoFormRows>("formRows", {
  required: true,
});

defineEmits<{
  enterEditMode: [];
}>();

const validationRules = {
  ncaaEligibilityCenterId: {
    data: { numeric, maxLength: maxLength(10), minLength: minLength(10) },
  },
  highlightsRecruitingProfileLink: {
    data: { url },
  },
};

const v$ = useVuelidate(validationRules, formRows);

const isInvalid = defineModel<boolean>("isInvalid", {
  required: true,
});

watch(v$, (newValue) => {
  isInvalid.value = newValue.$invalid;
});

const readModel = computed(() => {
  return {
    gpa: {
      label: "GPA",
      data: formRows.value?.gpa.data,
      display: filter.matches(
        "GPA",
        formRows.value?.gpa.data?.toString(),
        props.searchQuery
      ),
    },
    satScore: {
      label: "SAT Score",
      data: formRows.value?.satScore.data,
      display: filter.matches(
        "SAT Score",
        formRows.value?.satScore.data?.toString(),
        props.searchQuery
      ),
    },
    actScore: {
      label: "ACT Score",
      data: formRows.value?.actScore.data,
      display: filter.matches(
        "ACT Score",
        formRows.value?.actScore.data?.toString(),
        props.searchQuery
      ),
    },
    ncaaEligibilityCenterId: {
      label: "NCAA Eligibility Center ID",
      data: formRows.value?.ncaaEligibilityCenterId.data,
      display: filter.matches(
        "NCAA Eligibility Center ID",
        formRows.value?.ncaaEligibilityCenterId.data,
        props.searchQuery
      ),
    },
    collegeCommitment: {
      label: "College Commitment",
      data: formRows.value?.collegeCommitment.data,
      display: filter.matches(
        "College Commitment",
        formRows.value?.collegeCommitment.data,
        props.searchQuery
      ),
    },
    highlightsRecruitingProfileLink: {
      label: "Highlights/Recruiting Profile Link",
      data: formRows.value?.highlightsRecruitingProfileLink.data,
      display: filter.matches(
        "Highlights/Recruiting Profile Link",
        formRows.value?.highlightsRecruitingProfileLink.data,
        props.searchQuery
      ),
    },
  };
});
</script>
<style>
.input-number-fix input {
  width: 100%;
}
.input-number-fix span[data-pc-section="buttongroup"] {
  z-index: 11;
}
</style>
